import { AppBar, Badge, Box, Button, IconButton, Toolbar, Typography } from '@mui/material';
import {
    useLazyMallAction,
    useLazyMallIsBackArrow,
    useLazyMallIsObservedTop,
    useLazyMallNavTitle,
    useLazyMallNavType,
} from '@/store/lazyMallStore';
import MallIcon from './components/MallIcon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLoaderData, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useLoginFormDialogStore } from '@/store/loginFormDialogStore';
import { getCartListV2, getMallId, getMallPathWithProductGroupId, isLogined } from '@/services/mallUser.service';
import { useAnonymousCartStore } from '@/store/anonymousCartStore';
import { logOut } from '@/services/auth.service';
import { useUserStore } from '@/store/userStore';
import { useGuestUserCartStore } from '@/store/guestUserCartStore';
import useDialog from '@/hooks/useDialog';
import config from '@/config/config';

function Nav() {
    const isObservedTop = useLazyMallIsObservedTop();
    const { setAppBarHeight } = useLazyMallAction();
    const elRef = useRef<HTMLDivElement>(null);
    const navType = useLazyMallNavType();
    const navMap = {
        main: <MainNav />,
        backArrow: <BackArrowNav />,
    };

    useEffect(() => {
        if (!!elRef.current) {
            setAppBarHeight(elRef?.current?.clientHeight || 64);
        }
    }, []);

    return (
        <>
            <AppBar
                position={navType === 'main' ? 'fixed' : 'static'}
                sx={{
                    maxWidth: '720px',
                    left: 'auto',
                    right: 'auto',
                    backgroundColor: `${isObservedTop ? 'transparent' : '#fff'}`,
                    boxShadow: 'none',
                    borderBottom: `${navType === 'backArrow' ? '1px solid lightgray' : ''}`,
                }}
            >
                <Toolbar>{navMap[navType]}</Toolbar>
            </AppBar>
        </>
    );
}
export default Nav;

function MainNav() {
    const [searchParams, setSearchParams] = useSearchParams();
    const isObservedTop = useLazyMallIsObservedTop();
    const loaderData = useLoaderData() as any;
    const navigate = useNavigate();
    const params = useParams();
    const [path, setPath] = useState('');

    const { setIsOpen, setReferrer } = useLoginFormDialogStore((state) => state.action);
    const [numOfProductInCart, setNumOfProductInCart] = useState(0);
    const { cartInfo: anonymousCartInfo, value2RefreshOfCart } = useAnonymousCartStore((state) => {
        const { cartInfo, value2RefreshOfCart } = state;
        return { cartInfo, value2RefreshOfCart };
    });
    const { confirm, alert } = useDialog();

    const showConfirm = useCallback(async (dialogText: string) => {
        return await confirm(dialogText).then((r) => r);
    }, []);
    const mallUserId = useUserStore((state) => state.mallUserId);
    const { cartInfo } = useGuestUserCartStore();
    async function generatePath(): Promise<string> {
        if (!!params.path) return params.path || '';
        else {
            if (window.location.href.includes('product-details')) {
                return await getMallPathWithProductGroupId(params.productId || '');
            } else return '';
        }
    }

    useEffect(() => {
        (async () => {
            try {
                const path = await generatePath();
                if (!path) return;
                setPath(path);
                const { mallId } = await getMallId(path || '');
                if (isLogined()) {
                    const { item: info } = await getCartListV2(mallId, mallUserId);
                    const sellerCount = Number((info?.sellerProductList || [])?.length || 0);
                    const venderCount =
                        (info?.vendorProductList || []).reduce(
                            (acc, item) => acc + ((item?.productList || [])?.length || 0),
                            0,
                        ) || 0;
                    setNumOfProductInCart(Number(sellerCount + venderCount));
                } else {
                    const sellerCount = Number(cartInfo[mallId]?.sellerProductList?.length || 0);
                    const venderCount =
                        cartInfo[mallId]?.vendorProductList.reduce(
                            (acc, item) => acc + (item?.productList?.length || 0),
                            0,
                        ) || 0;
                    setNumOfProductInCart(Number(sellerCount + venderCount));
                }
            } catch (error) {
                setNumOfProductInCart(0);
            }
        })();
    }, [value2RefreshOfCart, mallUserId]);
    const checkParams = useMemo(() => {
        return Object.keys(params).length === 0;
    }, [window.location]);

    return (
        <>
            <IconButton
                onClick={async () => {
                    const utmMedium = searchParams.get('utm_medium');
                    if (utmMedium) {
                        navigate(`/${utmMedium}`);
                        return;
                    }

                    if (params?.path && Object.keys(params).length == 1) {
                        navigate(`/${params.path}`);
                        return;
                    }
                    let where2Go = '';
                    if (path) {
                        where2Go = path === 'hooking_soup' ? `/mall` : `/mall/${path}`;
                    } else {
                        where2Go = `/mall`;
                    }
                    navigate(where2Go);
                }}
            >
                <MallIcon
                    iconName="home"
                    size="nav"
                    color={isObservedTop ? 'white' : 'black'}
                    style={{
                        filter: 'drop-shadow(0px 0px 1px rgba(0,0,0,0.5))',
                    }}
                />
            </IconButton>
            <Box flexGrow={1} />
            {(!!params.path || location.href.includes('product-details')) && (
                <Box>
                    {!!params.path && (
                        <IconButton
                            onClick={async () => {
                                // const path = await generatePath()
                                navigate(`/mall/${path}/search`);
                            }}
                        >
                            <MallIcon
                                iconName="search"
                                size="nav"
                                color={isObservedTop ? 'white' : 'black'}
                                style={{
                                    filter: 'drop-shadow(0px 0px 1px rgba(0,0,0,0.5))',
                                }}
                            />
                        </IconButton>
                    )}
                    <IconButton
                        onClick={async () => {
                            // const path = await generatePath()
                            navigate(`/mall/${path}/mypage`);
                        }}
                    >
                        <MallIcon
                            iconName="user"
                            size="nav"
                            color={isObservedTop ? 'white' : 'black'}
                            style={{
                                filter: 'drop-shadow(0px 0px 1px rgba(0,0,0,0.5))',
                            }}
                        />
                    </IconButton>

                    <IconButton
                        onClick={async () => {
                            // const path = await generatePath()
                            navigate(`/mall/${path}/cart`);
                        }}
                    >
                        <Badge
                            badgeContent={numOfProductInCart}
                            color="primary"
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            sx={{
                                '& .MuiBadge-badge': {
                                    width: '20px',
                                    height: '15px',
                                    right: 1,
                                    bottom: 8,
                                    fontSize: '0.6rem',
                                },
                            }}
                        >
                            <MallIcon
                                iconName="bag"
                                size="nav"
                                color={isObservedTop ? 'white' : 'black'}
                                style={{
                                    filter: 'drop-shadow(0px 0px 1px rgba(0,0,0,0.5))',
                                }}
                            />
                        </Badge>
                    </IconButton>
                </Box>
            )}
            {checkParams && (
                <Box display={'flex'} alignItems={'center'}>
                    {/* <IconButton
                        onClick={async () => {
                            setReferrer('/mall')
                            setIsOpen(true)
                        }}
                    >
                        <MallIcon
                            iconName="user"
                            size="nav"
                            color={isObservedTop ? 'white' : 'black'}
                            style={{
                                filter: 'drop-shadow(0px 0px 1px rgba(0,0,0,0.5))',
                            }}
                        />
                    </IconButton> */}
                    {isLogined() && (
                        <Typography variant="h3" marginRight={'5px'} color={isObservedTop ? 'white' : 'black'}>
                            <b>{!!loaderData.userName ? loaderData.userName : ''}님 </b>
                        </Typography>
                    )}
                    <Button
                        onClick={async () => {
                            if (isLogined()) {
                                if (!(await showConfirm('로그아웃 하시겠습니까?').then((r) => r))) return;
                                try {
                                    navigate(`/logout?referer=/mall`);
                                } catch (error) {
                                    throw error;
                                }
                            } else {
                                setReferrer('/mall');
                                setIsOpen(true);
                            }
                        }}
                        size="small"
                        sx={{ border: `1px solid ${isObservedTop ? 'white' : 'black'}` }}
                        variant="text"
                    >
                        <Typography variant="h5" color={isObservedTop ? 'white' : 'black'}>
                            <b>{isLogined() ? '로그아웃' : '로그인'}</b>
                        </Typography>
                    </Button>
                </Box>
            )}
        </>
    );
}

function BackArrowNav() {
    const title = useLazyMallNavTitle();
    const navigate = useNavigate();
    const isBackArrow = useLazyMallIsBackArrow();
    const checkPopup = !isNaN(window.history.state?.idx) && Number(window.history.state?.idx) === 0;
    return (
        <>
            <Box width={'20%'}>
                {isBackArrow && (
                    <>
                        {!checkPopup && (
                            <IconButton
                                onClick={() => {
                                    navigate(-1);
                                }}
                            >
                                <MallIcon iconName="back-arrow" size="nav" />
                            </IconButton>
                        )}
                    </>
                )}
            </Box>
            <Box display={'flex'} justifyContent="center" alignItems={'center'} flexGrow={1} zIndex={900} color="black">
                <Typography fontSize={'1.1rem'}>
                    <b>{title}</b>
                </Typography>
            </Box>
            <Box width={'20%'} />
        </>
    );
}
